// Set_Proposal review state (blueprint-sets Req 10.2, 10.3, 10.5, 11.5; design "lib/proposalState.ts").
// Pure and JSON-serializable: the review page keeps it in sessionStorage (`setProposal:<runId>`), and nothing here
// talks to the API. The request builders produce the bodies of "Preview set" and "Create set".
import {
  BLUEPRINT_NAME_PATTERN,
  MAX_DESCRIPTION,
  NAME_MESSAGE,
  mergeFlagsOf,
  mergeTypes,
  noDescriptionMessage,
  normalizeDocType,
  sameTypeMessage,
  validateForm,
  type BlueprintFieldNode,
  type MergeFlag,
  type ProposedType,
  type SetProposal,
  type VersionRef,
  type createSetFromProposalRequest,
  type createSetPreviewRequest,
} from '@bda/shared';
import type { z } from 'zod';

export type CreateSetPreviewRequest = z.infer<typeof createSetPreviewRequest>;
export type CreateSetFromProposalRequest = z.infer<typeof createSetFromProposalRequest>;

export type ReviewUse = { kind: 'new' } | { kind: 'existing'; blueprintId: string; name: string; ref: VersionRef };

export interface ReviewType extends ProposedType {
  included: boolean;
  /** A new draft blueprint from this type's fields, or an existing blueprint chosen for it (Req 10.2). */
  use: ReviewUse;
  /** Indices into `fields` the author excluded, sorted. */
  excludedFields: number[];
}

export interface ReviewState {
  runId: string;
  /** Page count of the Sample_Packet; every page 1..pages is in one type, `unassigned`, or `unclassified`. */
  pages: number;
  name: string;
  description: string;
  types: ReviewType[];
  unassigned: SetProposal['unassigned'];
  unclassified: SetProposal['unclassified'];
  /** The model's flags as proposed; `mergeFlags` is derived from them and the current document types. */
  modelFlags: MergeFlag[];
  /** Current Merge_Flags: SAME_TYPE for equal document types, then the model's flags between remaining types. */
  mergeFlags: MergeFlag[];
}

export type ReviewAction =
  | { type: 'setMeta'; name?: string; description?: string }
  | { type: 'editType'; key: string; name?: string; documentType?: string; description?: string }
  | { type: 'setFields'; key: string; fields: BlueprintFieldNode[] }
  | { type: 'toggleField'; key: string; index: number }
  | { type: 'merge'; keep: string; remove: string }
  | { type: 'movePages'; pages: number[]; to: string | 'unassigned' }
  | { type: 'remove'; key: string }
  | { type: 'include'; key: string; included: boolean }
  /** `ref` defaults to 'DRAFT'; pass `latestVersion ?? 'DRAFT'` from `existingMatches` so a preview uses what create will. */
  | { type: 'useExisting'; key: string; blueprintId: string; name: string; ref?: VersionRef }
  | { type: 'useNew'; key: string };

export const MOVED_REASON = 'Moved here during review.';
export const REMOVED_REASON = 'Its document type was removed during review.';

export function initialReview(p: SetProposal, setName: string): ReviewState {
  const types: ReviewType[] = p.types.map((t) => ({ ...t, included: true, use: { kind: 'new' }, excludedFields: [] }));
  return {
    runId: p.runId,
    pages: p.pages,
    name: setName,
    description: '',
    types,
    unassigned: p.unassigned.map((u) => ({ ...u })),
    unclassified: p.unclassified.map((u) => ({ ...u })),
    modelFlags: p.mergeFlags.filter((f) => f.source === 'MODEL'),
    mergeFlags: mergeFlagsOf(
      types,
      p.mergeFlags.filter((f) => f.source === 'MODEL'),
    ),
  };
}

const sortNum = (xs: number[]): number[] => [...new Set(xs)].sort((a, b) => a - b);
const byPage = (a: { page: number }, b: { page: number }) => a.page - b.page;

function withTypes(s: ReviewState, types: ReviewType[]): ReviewState {
  return { ...s, types, mergeFlags: mergeFlagsOf(types, s.modelFlags) };
}

function mapType(s: ReviewState, key: string, fn: (t: ReviewType) => ReviewType): ReviewState {
  if (!s.types.some((t) => t.key === key)) return s;
  return withTypes(
    s,
    s.types.map((t) => (t.key === key ? fn(t) : t)),
  );
}

/**
 * Keeps exclusions attached to the same fields after a FieldsEditor change: a node that is still in the list (same
 * object) keeps its exclusion at its new index; with an unchanged length, an edited node keeps it at its index.
 */
function remapExcluded(prev: BlueprintFieldNode[], next: BlueprintFieldNode[], excluded: number[]): number[] {
  const out: number[] = [];
  for (const i of excluded) {
    const node = prev[i];
    const j = node ? next.indexOf(node) : -1;
    if (j >= 0) out.push(j);
    else if (next.length === prev.length && i < next.length) out.push(i);
  }
  return sortNum(out);
}

function merge(s: ReviewState, keepKey: string, removeKey: string): ReviewState {
  if (keepKey === removeKey) return s;
  const keep = s.types.find((t) => t.key === keepKey);
  const gone = s.types.find((t) => t.key === removeKey);
  if (!keep || !gone) return s;
  const merged = mergeTypes(keep, gone);
  // `mergeTypes` keeps the first type's fields in order, then appends the second's new names in order.
  const excluded = [...keep.excludedFields];
  const goneExcluded = new Set(gone.excludedFields.map((i) => gone.fields[i]).filter((f) => f !== undefined));
  merged.fields.forEach((f, i) => {
    if (i >= keep.fields.length && goneExcluded.has(f)) excluded.push(i);
  });
  const next: ReviewType = { ...merged, included: keep.included, use: keep.use, excludedFields: sortNum(excluded) };
  return withTypes(
    s,
    s.types.filter((t) => t.key !== removeKey).map((t) => (t.key === keepKey ? next : t)),
  );
}

function movePages(s: ReviewState, pages: number[], to: string): ReviewState {
  const valid = sortNum(pages.filter((p) => Number.isInteger(p) && p >= 1 && p <= s.pages));
  if (valid.length === 0 || (to !== 'unassigned' && !s.types.some((t) => t.key === to))) return s;
  const moving = new Set(valid);
  const types = s.types.map((t) => {
    const kept = t.pages.filter((p) => !moving.has(p));
    const pagesNext = t.key === to ? sortNum([...kept, ...valid]) : kept;
    return pagesNext.length === t.pages.length && pagesNext.every((p, i) => p === t.pages[i])
      ? t
      : { ...t, pages: pagesNext };
  });
  const unassigned = s.unassigned.filter((u) => !moving.has(u.page));
  if (to === 'unassigned') unassigned.push(...valid.map((page) => ({ page, reason: MOVED_REASON })));
  return {
    ...withTypes(s, types),
    unassigned: unassigned.sort(byPage),
    unclassified: s.unclassified.filter((u) => !moving.has(u.page)),
  };
}

export function reviewReducer(s: ReviewState, a: ReviewAction): ReviewState {
  switch (a.type) {
    case 'setMeta':
      return { ...s, name: a.name ?? s.name, description: a.description ?? s.description };
    case 'editType':
      return mapType(s, a.key, (t) => ({
        ...t,
        name: a.name ?? t.name,
        documentType: a.documentType ?? t.documentType,
        description: a.description ?? t.description,
      }));
    case 'setFields':
      return mapType(s, a.key, (t) => ({
        ...t,
        fields: a.fields,
        excludedFields: remapExcluded(t.fields, a.fields, t.excludedFields),
      }));
    case 'toggleField':
      return mapType(s, a.key, (t) => {
        if (!Number.isInteger(a.index) || a.index < 0 || a.index >= t.fields.length) return t;
        const on = t.excludedFields.includes(a.index);
        return {
          ...t,
          excludedFields: on ? t.excludedFields.filter((i) => i !== a.index) : sortNum([...t.excludedFields, a.index]),
        };
      });
    case 'merge':
      return merge(s, a.keep, a.remove);
    case 'movePages':
      return movePages(s, a.pages, a.to);
    case 'remove': {
      const gone = s.types.find((t) => t.key === a.key);
      if (!gone) return s;
      return {
        ...withTypes(
          s,
          s.types.filter((t) => t.key !== a.key),
        ),
        unassigned: [...s.unassigned, ...gone.pages.map((page) => ({ page, reason: REMOVED_REASON }))].sort(byPage),
      };
    }
    case 'include':
      return mapType(s, a.key, (t) => ({ ...t, included: a.included }));
    case 'useExisting':
      return mapType(s, a.key, (t) => ({
        ...t,
        use: { kind: 'existing', blueprintId: a.blueprintId, name: a.name, ref: a.ref ?? 'DRAFT' },
      }));
    case 'useNew':
      return mapType(s, a.key, (t) => ({ ...t, use: { kind: 'new' } }));
  }
}

/** The fields a new type's blueprint gets: all but the excluded ones. */
export const includedFields = (t: ReviewType): BlueprintFieldNode[] =>
  t.fields.filter((_, i) => !t.excludedFields.includes(i));

const label = (t: ReviewType): string => t.name.trim() || t.key;

/**
 * Req 10.5: why "Create set" is unavailable. Empty when it can be created. `key` is the type a reason belongs to, or
 * null for the set as a whole. Checks: the set name and description, at least one and at most `maxMembers` included
 * types, no two included types with the same document type, and each included new type passes `validateForm` (with
 * its included fields) and has a description, as the API's set rules require.
 */
export function createBlockers(s: ReviewState, maxMembers: number): Array<{ key: string | null; message: string }> {
  const out: Array<{ key: string | null; message: string }> = [];
  if (!BLUEPRINT_NAME_PATTERN.test(s.name) || s.name.trim().length === 0)
    out.push({ key: null, message: `Set name: ${NAME_MESSAGE}` });
  if (s.description.length > MAX_DESCRIPTION)
    out.push({ key: null, message: `Set description must be at most ${MAX_DESCRIPTION} characters.` });
  const included = s.types.filter((t) => t.included);
  if (included.length === 0) out.push({ key: null, message: 'Include at least one document type.' });
  else if (included.length > maxMembers)
    out.push({ key: null, message: `A set can have at most ${maxMembers} blueprints (${included.length} included).` });
  included.forEach((t, j) => {
    const type = normalizeDocType(t.documentType);
    const first = included.slice(0, j).find((o) => normalizeDocType(o.documentType) === type);
    if (first && type.length > 0) out.push({ key: t.key, message: sameTypeMessage(label(first), label(t), type) });
    if (t.use.kind !== 'new') return;
    const errors = Object.values(
      validateForm({
        name: t.name,
        documentType: t.documentType,
        description: t.description,
        fields: includedFields(t),
      }),
    );
    if (errors.length > 0) {
      const more = errors.length > 1 ? ` (and ${errors.length - 1} more)` : '';
      out.push({ key: t.key, message: `${label(t)}: ${errors[0]}${more}` });
    } else if (t.description.trim().length === 0) out.push({ key: t.key, message: noDescriptionMessage(label(t)) });
  });
  return out;
}

function memberOf(t: ReviewType): CreateSetPreviewRequest['members'][number] {
  if (t.use.kind === 'existing')
    return { kind: 'existing', key: t.key, blueprintId: t.use.blueprintId, ref: t.use.ref };
  return {
    kind: 'proposed',
    key: t.key,
    name: t.name,
    documentType: t.documentType,
    description: t.description,
    fields: includedFields(t),
  };
}

/** "Preview set" body (Req 11.1); `canonicalJson` of it is the preview's basis key (Req 11.5). Included types only. */
export function toPreviewRequest(s: ReviewState): CreateSetPreviewRequest {
  return { sourceRunId: s.runId, members: s.types.filter((t) => t.included).map(memberOf) };
}

/** "Create set" body (Req 10.6). Included types only. */
export function toCreateRequest(s: ReviewState): CreateSetFromProposalRequest {
  return { name: s.name, description: s.description, types: s.types.filter((t) => t.included).map(memberOf) };
}
